import React from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { Box, Typography, Tooltip, Avatar, Stack, Paper } from "@mui/material";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import InputRoundedIcon from '@mui/icons-material/InputRounded';

const StyledColumn = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
};

const StyledList = {
  backgroundColor: "#F8FAFC",
  borderRadius: 12,
  padding: 8, // Minimal padding
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  minHeight: "400px", // Taller to fit more items
  maxHeight: "65vh",
  overflowY: "auto",
  border: "1px solid #E2E8F0",
  boxShadow: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)",
};

const StyledItem = {
  backgroundColor: "white",
  padding: "8px 12px", // Very compact padding
  margin: "0 0 4px 0", // Minimal margin
  borderRadius: 6,
  border: "1px solid #F1F5F9",
  transition: "all 0.15s ease",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  "&:hover": {
    backgroundColor: "#F8FAFC",
    borderColor: "#CBD5E1",
  },
};

const Column = ({ col: { list, id } }) => {
  const getUniqueThreatsAndVulns = (instancias) => {
    const threats = new Set(
      instancias
        .map((item) => item.ame_nombre || "Sin amenaza definida")
        .filter(Boolean)
    );
    const vulnerabilities = new Set(
      instancias
        .map((item) => item.vul_nombre || "Sin vulnerabilidad definida")
        .filter(Boolean)
    );
    return {
      threats: Array.from(threats),
      vulnerabilities: Array.from(vulnerabilities),
    };
  };

  return (
    <Droppable droppableId={id}>
      {(provided) => (
        <Box sx={StyledColumn}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            px: 0.5
          }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}
            >
              {id}
            </Typography>
            <Box sx={{
              backgroundColor: '#E2E8F0',
              borderRadius: '4px',
              px: 0.75,
              py: 0.25,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#475569'
            }}>
              {list.length}
            </Box>
          </Box>

          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={StyledList}
          >
            {list.length === 0 && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#94A3B8',
                textAlign: 'center',
                p: 2,
                gap: 1
              }}>
                <Box sx={{
                  p: 1.5,
                  borderRadius: '50%',
                  backgroundColor: '#F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#CBD5E1'
                }}>
                  {id === "Activos" ? <FolderOpenRoundedIcon /> : <InputRoundedIcon />}
                </Box>
                <Typography variant="caption" fontWeight={500}>
                  {id === "Activos"
                    ? "Sin activos"
                    : "Arrastra aquí"}
                </Typography>
              </Box>
            )}
            {list.map((item, index) => {
              const { threats, vulnerabilities } = getUniqueThreatsAndVulns(
                item.instancias
              );
              return (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided, snapshot) => (
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" fontWeight="bold" display="block">
                            {item.nombre}
                          </Typography>
                        </Box>
                      }
                      placement="left"
                      arrow
                    >
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        elevation={snapshot.isDragging ? 3 : 0}
                        sx={{
                          ...StyledItem,
                          opacity: snapshot.isDragging ? 0.9 : 1,
                          backgroundColor: snapshot.isDragging ? '#F1F5F9' : 'white',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: '#334155',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '65%',
                            fontSize: '0.85rem'
                          }}
                        >
                          {item.nombre}
                        </Typography>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {/* Threat Bubble */}
                          {threats.length > 0 && (
                            <Box sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: '#DBEAFE',
                              color: '#1D4ED8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 700
                            }}>
                              {threats.length}
                            </Box>
                          )}

                          {/* Vulnerability Bubble */}
                          {vulnerabilities.length > 0 && (
                            <Box sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: '#FCE7F3',
                              color: '#BE185D',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 700
                            }}>
                              {vulnerabilities.length}
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    </Tooltip>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Box>
        </Box>
      )}
    </Droppable>
  );
};

export default Column;
